import { partition, pick } from '@exodus/basic-utils';
import { Timer } from '@exodus/timer';
import { url } from '@exodus/fetch/url';
import createFetchival from '@exodus/fetch/experimental/create-fetchival';
import delay from 'delay';
import EventEmitter from 'events/events.js';
import ms from 'ms';
import typeforce from '@exodus/typeforce';
import { batchProcessOrders, getOrderUpdates, shouldCheckOrder } from './utils/index.js';
import filterChanges from './filter-changes.js';
import { FAIL_LIMIT, FAILED_PATIENCE_WINDOW, FINAL_ORDER_STATES, ORDER_STATUS_UPDATE_STRATEGY, START_BUFFER, V3_ORDER_FINISHED_STATUSES, } from './constants.js';
class OrdersMonitor extends EventEmitter {
    #appName;
    #appVersion;
    #appPlatform;
    #assetsModule;
    #availableAssetNamesAtom;
    #baseUrl;
    #txLogsAtom;
    #isMoneroSyncing;
    #logger;
    #optimisticActivityEnabled;
    #ordersAtom;
    #timer;
    #txTypes;
    #fetchival;
    #orderStatusUpdateStrategy;
    #cachedFinishedV3Orders;
    #firstMissAtAtom;
    #restoreAtom;
    startTime;
    constructor(opts) {
        super();
        typeforce({
            assetsModule: 'Object',
            availableAssetNamesAtom: 'Object',
            txLogsAtom: 'Object',
            config: {
                appName: 'String',
                appVersion: 'String',
                appPlatform: '?String',
                baseUrl: 'String',
                interval: 'Number',
                optimisticActivityEnabled: 'Boolean',
                orderStatusUpdateStrategy: typeforce.maybe(typeforce.oneOf(...Object.values(ORDER_STATUS_UPDATE_STRATEGY).map((v) => typeforce.value(v)))),
            },
            isMoneroSyncing: '?Function',
            logger: 'Object',
            ordersAtom: 'Object',
            firstMissAtAtom: 'Object',
            fetch: 'Function',
        }, opts);
        this.#appName = opts.config.appName;
        this.#appVersion = opts.config.appVersion;
        this.#appPlatform = opts.config.appPlatform;
        this.#assetsModule = opts.assetsModule;
        this.#availableAssetNamesAtom = opts.availableAssetNamesAtom;
        this.#baseUrl = new URL(opts.config.baseUrl);
        this.#txLogsAtom = opts.txLogsAtom;
        this.#isMoneroSyncing = opts.isMoneroSyncing ?? (async () => false);
        this.#logger = opts.logger;
        this.#optimisticActivityEnabled = opts.config.optimisticActivityEnabled;
        this.#ordersAtom = opts.ordersAtom;
        this.#timer = new Timer(opts.config.interval);
        this.#txTypes = opts.config.txTypes;
        this.#fetchival = createFetchival({ fetch: opts.fetch });
        this.#orderStatusUpdateStrategy =
            opts.config.orderStatusUpdateStrategy || ORDER_STATUS_UPDATE_STRATEGY.CLIENT_AND_SERVER;
        this.#cachedFinishedV3Orders = new Map();
        this.#firstMissAtAtom = opts.firstMissAtAtom;
        this.#restoreAtom = opts.restoreAtom;
    }
    start = () => {
        this.startTime = new Date();
        this.#timer.start(this._tick);
    };
    stop = () => this.#timer.stop();
    on(event, listener) {
        return super.on(event, listener);
    }
    off(event, listener) {
        super.removeListener(event, listener);
        return this;
    }
    tick = () => this.#timer.tick();
    checkOrders = async (orderIds) => {
        const idsArray = Array.isArray(orderIds) ? orderIds : [orderIds];
        if (idsArray.length === 0)
            return;
        const allOrders = await this.#ordersAtom.get();
        const ordersToCheck = [...allOrders].filter((order) => idsArray.includes(order.orderId));
        if (ordersToCheck.length === 0)
            return;
        await this.#processOrderUpdates(ordersToCheck, { manualCheck: true });
    };
    _tick = async () => {
        const allOrders = await this.#ordersAtom.get();
        await this.#pruneFirstMissAt(allOrders);
        const ordersToCheck = await this.#getOrdersToCheck();
        this.#logger.debug(`monitor tick: ${ordersToCheck.length} orders to check out of ${allOrders.size} total`);
        await this.#processOrderUpdates(ordersToCheck);
    };
    #pruneFirstMissAt = async (active) => {
        const activeIds = new Set([...active]
            .filter((order) => !FINAL_ORDER_STATES.has(order.exodusStatus))
            .map((order) => order.orderId));
        await this.#firstMissAtAtom.set((prev) => {
            const next = {};
            let changed = false;
            for (const [id, t] of Object.entries(prev)) {
                if (activeIds.has(id))
                    next[id] = t;
                else
                    changed = true;
            }
            return changed ? next : prev;
        });
    };
    #processOrderUpdates = async (ordersToCheck, { manualCheck = false } = {}) => {
        let v3Orders;
        try {
            v3Orders = await batchProcessOrders(ordersToCheck.filter((order) => !this.#cachedFinishedV3Orders.has(order.orderId)), async (batch) => {
                const [chainedOrderIds, v3OrderIds] = partition(batch, (order) => order.svc === 'chained');
                const orders = await Promise.all([
                    this.#getOrdersFromServer({
                        orderIds: chainedOrderIds.map((order) => order.orderId),
                        isChained: true,
                    }),
                    this.#getOrdersFromServer({
                        orderIds: v3OrderIds.map((order) => order.orderId),
                        isChained: false,
                    }),
                ]);
                return orders.flat();
            });
        }
        catch (err) {
            this.#logger.warn('orders monitor: skipping tick due to fetch failure', err);
            return;
        }
        const isRestoringAssets = (await this.#restoreAtom.get()) ?? false;
        if (ordersToCheck.length === 0 && v3Orders.length === 0) {
            this.#cachedFinishedV3Orders.clear();
        }
        const ordersChangesPromises = ordersToCheck.map(async (order) => {
            const changes = Object.create(null);
            try {
                const v3Order = v3Orders.find((v3Order) => {
                    const v3OrderId = order.svc === 'chained' ? v3Order.id : v3Order.providerOrderId;
                    return v3OrderId === order.orderId;
                }) || this.#cachedFinishedV3Orders.get(order.orderId);
                if (v3Order) {
                    await this.#clearFirstMissAt(order.orderId);
                    if (v3Order.message && v3Order.message !== order.message) {
                        changes.message = v3Order.message;
                    }
                    const toTx = await this.#findToTx(order);
                    const isIndexlessOrder = await this.#getIsIndexlessOrder(order);
                    const finished = V3_ORDER_FINISHED_STATUSES.has(v3Order.status);
                    if (finished) {
                        this.#cachedFinishedV3Orders.set(order.orderId, v3Order);
                    }
                    else {
                        const isExpired = await this.#isOrderExpired(order);
                        if (isExpired) {
                            changes.status = 'failed-final';
                        }
                    }
                    if (order.fromAsset && order.toAsset) {
                        const fromAsset = this.#assetsModule.getAsset(order.fromAsset);
                        const toAsset = this.#assetsModule.getAsset(order.toAsset);
                        if (fromAsset && toAsset) {
                            const updates = getOrderUpdates({
                                v3Order,
                                toTx,
                                isIndexlessOrder,
                                txTypes: this.#txTypes,
                                order,
                                orderStatusUpdateStrategy: this.#orderStatusUpdateStrategy,
                                isRestoringAssets,
                                fromAsset,
                                toAsset,
                            });
                            Object.assign(changes, updates);
                        }
                    }
                }
                else if (!manualCheck && (await this.#shouldFinalizeFailed(order))) {
                    changes.status = 'failed-final';
                    await this.#clearFirstMissAt(order.orderId);
                }
            }
            catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);
                const stack = err instanceof Error ? err.stack : undefined;
                this.#logger.warn('Orders monitor fetchOrderData error', order, errorMessage, stack);
            }
            await delay(ms('250ms'));
            const filteredChanges = filterChanges({
                order,
                changes,
                optimisticActivityEnabled: this.#optimisticActivityEnabled,
            });
            return { order, changes: filteredChanges };
        });
        const ordersChanges = await Promise.all(ordersChangesPromises);
        const changes = ordersChanges.filter(({ changes }) => Object.keys(changes).length > 0);
        if (changes.length === 0)
            return;
        this.#logger.debug(`monitor: ${changes.length} orders updated out of ${ordersToCheck.length} checked`);
        this.emit('orders-bulk-update', changes);
    };
    #getIsIndexlessOrder = async (order) => {
        if (!order.toAsset)
            return false;
        const toAsset = this.#assetsModule.getAsset(order.toAsset);
        return Boolean(toAsset?.baseAsset?.api?.features?.noHistory);
    };
    #findTx = async ({ txId, walletAccount, assetName, }) => {
        if (!assetName || !txId || !walletAccount)
            return null;
        const { value: txLogs } = await this.#txLogsAtom.get();
        return txLogs[walletAccount]?.[assetName]?.get(txId) || null;
    };
    #findToTx = async (order) => {
        return this.#findTx({
            txId: order.toTxId,
            assetName: order.toAsset,
            walletAccount: order.toWalletAccount,
        });
    };
    #getOrdersToCheck = async () => {
        const orders = await this.#ordersAtom.get();
        const availableAssets = await this.#getAvailableAssets();
        return [...orders].filter((order) => shouldCheckOrder({ order, availableAssets }));
    };
    #getAvailableAssets = async () => pick(this.#assetsModule.getAssets(), await this.#availableAssetNamesAtom.get());
    #getOrdersFromServer = async ({ orderIds, isChained, }) => {
        if (orderIds.length === 0)
            return [];
        const endpoint = isChained ? 'chained-orders' : 'orders';
        return this.#request(endpoint).get(isChained ? { ids: orderIds, limit: orderIds.length } : { providerOrderIds: orderIds });
    };
    #request = (path) => this.#fetchival(url `${this.#baseUrl}/${path}`, {
        headers: {
            'App-Name': this.#appName,
            'App-Version': this.#appVersion,
            ...(this.#appPlatform ? { 'App-Platform': this.#appPlatform } : {}),
        },
    });
    #isDepositPending = async (order) => {
        if (!order.fromTxId || !order.fromAsset || !order.fromWalletAccount)
            return false;
        const { value: txLogs } = await this.#txLogsAtom.get();
        const tx = txLogs[order.fromWalletAccount]?.[order.fromAsset]?.get(order.fromTxId);
        return tx?.pending === true;
    };
    #shouldFinalizeFailed = async (order) => {
        const orderDate = order.date instanceof Date ? order.date.getTime() : new Date(order.date).getTime();
        if (Date.now() - orderDate < START_BUFFER)
            return false;
        if (await this.#isDepositPending(order))
            return false;
        const firstMissAt = await this.#firstMissAtAtom.get();
        if (!Object.hasOwn(firstMissAt, order.orderId)) {
            await this.#firstMissAtAtom.set((prev) => ({ ...prev, [order.orderId]: Date.now() }));
            return false;
        }
        return Date.now() - firstMissAt[order.orderId] > FAILED_PATIENCE_WINDOW;
    };
    #clearFirstMissAt = async (orderId) => {
        await this.#firstMissAtAtom.set((prev) => {
            if (!Object.hasOwn(prev, orderId))
                return prev;
            const { [orderId]: _omit, ...rest } = prev;
            return rest;
        });
    };
    #isOrderExpired = async (order) => {
        const now = Date.now();
        const orderDate = order.date instanceof Date ? order.date.getTime() : new Date(order.date).getTime();
        if (order.status !== 'complete-verified' && now - orderDate > FAIL_LIMIT) {
            const moneroSyncInProgress = await this.#isMoneroSyncing();
            if (!(order.toAsset === 'monero' && moneroSyncInProgress)) {
                return true;
            }
        }
        return false;
    };
}
const createOrdersMonitor = (args) => new OrdersMonitor({ ...args });
const ordersMonitorDefinition = {
    id: 'ordersMonitor',
    type: 'monitor',
    factory: createOrdersMonitor,
    dependencies: [
        'assetsModule',
        'availableAssetNamesAtom',
        'txLogsAtom',
        'config',
        'logger',
        'fetch',
        'ordersAtom',
        'firstMissAtAtom',
        'isMoneroSyncing?',
        'restoreAtom',
    ],
    public: true,
};
export default ordersMonitorDefinition;
